'use client';

/**
 * Loads Google Tag (GA4) — or GTM if a container ID is set — once when ALL of:
 *   1. site config has `analytics.gtag_enabled = true` and a valid measurement ID
 *      (or a `analytics.gtm_id` for GTM mode)
 *   2. cookie-consent provider has hydrated and the user opted in to the
 *      "analytics" category
 *
 * Mirrors the structure of `AdSenseScript` exactly so future maintainers can
 * read one and understand the other. GTM takes precedence when both IDs are
 * set — GTM is the more capable container and a configured GTM with no
 * GA stream is a valid pattern.
 *
 * Idempotent via a `data-ps-gtag` marker on <html>.
 */
import { useEffect } from 'react';
import { useSiteConfig } from '@/lib/site-config';
import { useCookieConsent } from '@/lib/cookie-consent';

export default function GtagScript() {
  const { config, ready } = useSiteConfig();
  const { analytics, hydrated } = useCookieConsent();

  useEffect(() => {
    if (!ready || !hydrated) return;
    const enabled = !!config['analytics.gtag_enabled'];
    const gtagId = String(config['analytics.gtag_id'] ?? '').trim();
    const gtmId  = String(config['analytics.gtm_id']  ?? '').trim();
    const html = document.documentElement;
    const activeId = gtmId || gtagId;
    const allowed = enabled && analytics && !!activeId;

    if (html.dataset.psGtag === activeId && allowed) return;

    if (!allowed) {
      delete html.dataset.psGtag;
      return;
    }

    if (gtmId) {
      // GTM bootstrap — single async script + dataLayer init.
      const init = document.createElement('script');
      init.text = `window.dataLayer=window.dataLayer||[];window.dataLayer.push({'gtm.start':new Date().getTime(),event:'gtm.js'});`;
      document.head.appendChild(init);
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
      document.head.appendChild(s);
    } else {
      // Bare gtag.js + GA4 config. anonymize_ip is harmless on GA4 (it's the
      // default since 2020) but explicit reads better in privacy reviews.
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gtagId)}`;
      document.head.appendChild(s);
      const init = document.createElement('script');
      init.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gtagId}',{anonymize_ip:true});`;
      document.head.appendChild(init);
    }
    html.dataset.psGtag = activeId;
  }, [ready, hydrated, analytics, config]);

  return null;
}
